import { FC } from "react";
import { FieldInputProps } from "react-final-form";
import { useSelector } from "react-redux";

import { Error } from "@livechat/design-system-icons";
import { Icon, Tag, Text } from "@livechat/design-system-react-components";

import PriorityIndicator from "components/priority/PriorityIndicator";
import { TICKET_PRIORITY_LABEL_MAP } from "constants/tickets/priorities";
import { TicketStatusFormattedValues } from "interfaces/entities/ticket";
import {
  IRuleActionAddFollowers,
  IRuleActionAddTags,
  IRuleActionAssign,
  IRuleActionMessage,
  IRuleActionPriority,
  IRuleActionRemoveFollowers,
  IRuleActionRemoveTags,
  IRuleActionSendFollowUp,
  IRuleActionSendRatingRequest,
  IRuleActionStatus,
} from "store/entities/rules/interfaces";
import { selectTags } from "store/entities/tags";

import { AccordionAgentTag } from "../../components/AccordionAgentTag";
import { AccordionTeamTag } from "../../components/AccordionTeamTag";
import { ActionFieldArrayElement } from "../interfaces";
import { isActionType } from "../utils";

interface IProps {
  input: FieldInputProps<ActionFieldArrayElement>;
}

export const ActionLabel: FC<IProps> = ({ input }) => {
  const tags = useSelector(selectTags);

  if (!input.value.type) {
    return null;
  }

  if (isActionType(input, "spam")) {
    return null;
  }

  if (
    isActionType<IRuleActionSendRatingRequest>(input, "sendRatingRequest") ||
    isActionType<IRuleActionSendFollowUp>(input, "sendFollowUp") ||
    isActionType<IRuleActionMessage>(input, "message")
  ) {
    return <Colon />;
  }

  if (isActionType<IRuleActionAssign>(input, "assign")) {
    return (
      <>
        <Colon />
        {input.value.teamID === null ? <Tag>Keep assigned team</Tag> : <AccordionTeamTag teamID={input.value.teamID} />}
        <AccordionAgentTag
          agentID={input.value.agentID}
          randomAgent={input.value.randomAgent}
          roundRobin={input.value.roundRobin}
          agentType="agent"
        />
      </>
    );
  }
  if (isActionType<IRuleActionStatus>(input, "status")) {
    const selectedStatus = input.value.status;

    return (
      <>
        <Colon />
        <Tag
          leftNode={selectedStatus ? undefined : <Icon source={Error} />}
          kind={selectedStatus ? "default" : "error"}
        >
          {selectedStatus ? TicketStatusFormattedValues[selectedStatus] : "Select status…"}
        </Tag>
      </>
    );
  }
  if (isActionType<IRuleActionPriority>(input, "priority")) {
    const selectedPriority = input.value.priority;
    const isPrioritySelected = selectedPriority !== undefined;

    return (
      <>
        <Colon />
        <Tag
          leftNode={isPrioritySelected ? <PriorityIndicator priority={selectedPriority} /> : <Icon source={Error} />}
          kind={isPrioritySelected ? "default" : "error"}
        >
          {isPrioritySelected ? TICKET_PRIORITY_LABEL_MAP[selectedPriority] : "Select priority…"}
        </Tag>
      </>
    );
  }
  if (isActionType<IRuleActionAddTags>(input, "addTags") || isActionType<IRuleActionRemoveTags>(input, "removeTags")) {
    const tagIDs = input.value.tagIDs;

    return (
      <>
        <Colon />
        {tagIDs.length && tagIDs.length > 0 ? (
          <>
            {tagIDs.map(tagId => {
              const tag = tags[tagId];

              if (!tag) {
                return (
                  <Tag key={tagId} kind="error" leftNode={<Icon source={Error} />}>
                    Tag removed
                  </Tag>
                );
              }

              return (
                <Tag key={tagId} kind="default">
                  {tag.name}
                </Tag>
              );
            })}
          </>
        ) : (
          <Tag kind="error" leftNode={<Icon source={Error} />}>
            Select tags…
          </Tag>
        )}
      </>
    );
  }
  if (
    isActionType<IRuleActionAddFollowers>(input, "addFollowers") ||
    isActionType<IRuleActionRemoveFollowers>(input, "removeFollowers")
  ) {
    const followersId = input.value.followers;

    return (
      <>
        <Colon />
        {followersId.length && followersId.length > 0 ? (
          <>
            {followersId.map(followerId => (
              <AccordionAgentTag
                key={followerId}
                agentID={followerId}
                randomAgent={false}
                roundRobin={false}
                agentType="follower"
              />
            ))}
          </>
        ) : (
          <Tag kind="error" leftNode={<Icon source={Error} />}>
            Select followers…
          </Tag>
        )}
      </>
    );
  }
};

const Colon: FC = () => <Text bold>:</Text>;
